import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { getAuthSession, setActiveEntityId, subscribeToAuthSession } from "../auth.js";
import PageContext from "../PageContext.jsx";

const Settings = () => {
  const [activeEntityId, setActiveEntityIdState] = useState(() => String(getAuthSession()?.entityId || "1"));
  const [selectedEntityId, setSelectedEntityId] = useState(() => String(getAuthSession()?.entityId || "1"));
  const [entities, setEntities] = useState([]);

  const [values, setValues] = useState({
    Entity: "",
    FirstName: "",
    LastName: "",
    UserName: "",
  });
  const [passwords, setPasswords] = useState({
    CurrentPassword: "",
    NewPassword: "",
    ConfirmPassword: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const loadCoreData = async (entityId) => {
    const [settingsRes, entitiesRes] = await Promise.all([
      apiClient.get("/settings/profile", { params: { entityId } }),
      apiClient.get("/entities"),
    ]);

    const entityRows = Array.isArray(entitiesRes.data) ? entitiesRes.data : [];

    setValues({
      Entity: settingsRes.data?.Entity || "",
      FirstName: settingsRes.data?.FirstName || "",
      LastName: settingsRes.data?.LastName || "",
      UserName: settingsRes.data?.UserName || "",
    });
    setEntities(entityRows);

    if (!entityRows.some((row) => String(row.EntityID) === String(entityId))) {
      const defaultEntityId = entityRows[0]?.EntityID;
      if (defaultEntityId) setSelectedEntityId(String(defaultEntityId));
    }
  };

  useEffect(() => subscribeToAuthSession(() => {
    const session = getAuthSession();
    const nextEntityId = String(session?.entityId || "1");
    setActiveEntityIdState(nextEntityId);
  }), []);

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError("");
      try {
        await loadCoreData(selectedEntityId);
      } catch (err) {
        setError("Failed to load settings.");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [selectedEntityId]);

  const handleChange = (e) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setSuccess("");
  };

  const handlePasswordChange = (e) => {
    setPasswords((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await apiClient.put("/settings/profile", values, { params: { entityId: selectedEntityId } });
      setSuccess("Settings saved.");
    } catch (err) {
      setError("Could not save settings.");
      setSuccess("");
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!passwords.CurrentPassword || !passwords.NewPassword || !passwords.ConfirmPassword) {
      setPasswordError("Fill in all password fields.");
      return;
    }

    if (passwords.NewPassword !== passwords.ConfirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }

    try {
      await apiClient.post("/auth/change-password", {
        CurrentPassword: passwords.CurrentPassword,
        NewPassword: passwords.NewPassword,
      });
      setPasswords({
        CurrentPassword: "",
        NewPassword: "",
        ConfirmPassword: "",
      });
      setPasswordSuccess("Password updated.");
    } catch (err) {
      setPasswordError(err?.response?.data?.message || "Could not update password.");
    }
  };

  const activeEntity = entities.find((item) => String(item.EntityID) === String(activeEntityId));
  const selectedEntity = entities.find((item) => String(item.EntityID) === String(selectedEntityId));

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / Settings
          </p>
        </div>
      </header>
      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Settings" },
        ]}
        note={
          selectedEntity
            ? `You are editing settings for ${selectedEntity.Description}.`
            : "Choose the entity you want to work in and update its profile settings."
        }
      />

      <section className="panel page-cta">
        <div className="section-heading">
          <div>
            <p className="eyebrow">New User</p>
            <h2>Suggested Setup Procedure</h2>
          </div>
          <Link className="btn-link" to="/GettingStarted">
            Open Getting Started
          </Link>
        </div>
        <p className="section-copy">
          If you are setting up a new entity from scratch, use the guided sequence before entering reports or relying on dashboard metrics.
        </p>
      </section>

      <section className="panel page-cta">
        <div className="settings-entity-header">
          <div>
            <h2>Current Working Entity</h2>
            <p className="settings-entity-current">{activeEntity?.Description || `Entity ${activeEntityId}`}</p>
          </div>
          <div className="settings-entity-switcher">
            <label htmlFor="settings-work-entity">Choose Entity To Work In</label>
            <div className="settings-entity-actions">
              <select
                id="settings-work-entity"
                value={selectedEntityId}
                onChange={(e) => {
                  setSelectedEntityId(e.target.value);
                  setSuccess("");
                }}
              >
                {entities.map((item) => (
                  <option key={item.EntityID} value={item.EntityID}>
                    {item.Description}
                  </option>
                ))}
              </select>
              <button
                className="btn"
                type="button"
                disabled={selectedEntityId === activeEntityId}
                onClick={() => {
                  setActiveEntityId(selectedEntityId);
                  setActiveEntityIdState(selectedEntityId);
                  setSuccess("Working entity updated.");
                }}
              >
                Use This Entity
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="panel form-card">
        {loading ? (
          <p className="notice notice-muted">Loading settings...</p>
        ) : (
          <div className="form">
            <p className="notice notice-muted">
              Editing settings for <strong>{selectedEntity?.Description || `Entity ${selectedEntityId}`}</strong>.
            </p>

            <label htmlFor="settings-entity">Entity Label</label>
            <input
              id="settings-entity"
              type="text"
              name="Entity"
              placeholder="e.g. Musa Household"
              value={values.Entity}
              onChange={handleChange}
            />

            <label htmlFor="settings-first-name">First Name</label>
            <input id="settings-first-name" type="text" name="FirstName" value={values.FirstName} onChange={handleChange} />

            <label htmlFor="settings-last-name">Last Name</label>
            <input id="settings-last-name" type="text" name="LastName" value={values.LastName} onChange={handleChange} />

            <label htmlFor="settings-user-name">User Name</label>
            <input id="settings-user-name" type="text" name="UserName" value={values.UserName} onChange={handleChange} />

            <div className="form-actions">
              <button className="btn" onClick={handleSave}>
                Save Settings
              </button>
              <Link className="btn-link" to="/">
                Cancel
              </Link>
            </div>

            {error && <p className="notice notice-error">{error}</p>}
            {success && <p className="notice notice-muted">{success}</p>}
          </div>
        )}
      </section>

      <section className="panel form-card">
        <div className="form">
          <h2>Change Password</h2>

          <label htmlFor="settings-current-password">Current Password</label>
          <input
            id="settings-current-password"
            type="password"
            name="CurrentPassword"
            value={passwords.CurrentPassword}
            onChange={handlePasswordChange}
          />

          <label htmlFor="settings-new-password">New Password</label>
          <input
            id="settings-new-password"
            type="password"
            name="NewPassword"
            value={passwords.NewPassword}
            onChange={handlePasswordChange}
          />

          <label htmlFor="settings-confirm-password">Confirm New Password</label>
          <input
            id="settings-confirm-password"
            type="password"
            name="ConfirmPassword"
            value={passwords.ConfirmPassword}
            onChange={handlePasswordChange}
          />

          <div className="form-actions">
            <button className="btn" onClick={handlePasswordSave}>
              Update Password
            </button>
          </div>

          {passwordError && <p className="notice notice-error">{passwordError}</p>}
          {passwordSuccess && <p className="notice notice-muted">{passwordSuccess}</p>}
        </div>
      </section>

      {!loading && (
        <section className="panel page-cta">
          <h2>Administration</h2>
          <p className="notice notice-muted">
            Entity setup, user creation, and entity access management now live on the dedicated entities reference page.
          </p>
          <div className="form-actions">
            <Link className="btn-link" to="/Entities">
              Open Entity Management
            </Link>
            <Link className="btn-link" to="/Backups">
              Open Backups
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default Settings;
